import React, { ChangeEvent, useContext, useState } from "react";
import ChevronToggle from "../../shared/ChevronToggle/ChevronToggle";
import OwTitle from "../../shared/OwTitle/OwTitle";
import * as _ from "lodash-es";
import OwLabel from "../../shared/OwLabel/OwLabel";
import Typeahead from "../../shared/Typeahead/Typeahead";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import OwButton from "../../shared/OwButton/OwButton";
import UIService from "../../../services/UIService";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import UserTypesEnum from "../../../models/UserTypesEnum";
import ReportTypeEnum from "../../../models/ReportTypeEnum";
import ICustom_Report from "../../../models/ICustom_Report";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import OwDateRange from "../../shared/OwDateRange/OwDateRange";
import { Moment } from "moment";

type Props = {
    customReports: ICustom_Report[];
    performSearch: (queryString: string) => void;
};

interface IReportOption {
    value: ReportTypeEnum;
    title: string;
    guid?: string;
}

export const ReportsReadyFilter = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [isOpen, setIsOpen] = useState(true);
    const [title, setTitle] = useState("");
    const [reportType, setReportType] = useState("");
    const [createdAtStart, setCreatedAtStart] = useState<Moment | null>(null);
    const [createdAtEnd, setCreatedAtEnd] = useState<Moment | null>(null);

    let cardClass = "dark-card text-white bg-secondary";

    if (UIService.isMainViewLight()) {
        cardClass = "";
    }

    const toggleVisible = () => {
        setIsOpen(!isOpen);
    };

    const handleChange = (e: any) => {
        const name = e.target.name;
        const value = e.target.value;

        switch (name) {
            case "title":
                setTitle(value);
                break;
            case "report_type":
                setReportType(value);
                break;
        }
    };

    const getQueryString = () => {
        const query: string[] = [];
        let queryString = "";

        if (!_.isNil(title) && title.length) {
            query.push(`title=${title}`);
        }

        if (!_.isNil(reportType) && reportType.length) {
            query.push(`report_type=${reportType}`);
        }

        if (!_.isNil(createdAtStart) && createdAtStart && !_.isNil(createdAtEnd) && createdAtEnd) {
            query.push(`created_at_range_start=${createdAtStart.format("YYYY-MM-DD")}`);
            query.push(`created_at_range_end=${createdAtEnd.format("YYYY-MM-DD")}`);
        }

        if (query.length) {
            queryString = "?";

            queryString += query.join("&");
        }

        return queryString;
    };

    const performSearch = () => {
        const queryString = getQueryString();

        props.performSearch(queryString);
    };

    const enterSearch = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
            performSearch();
        }
    };

    const resetSearch = () => {
        setTitle("");
        setReportType("");
        setCreatedAtEnd(null);
        setCreatedAtStart(null);
        props.performSearch("");
    };

    const getReportOptions = () => {
        const _reportOptions: IReportOption[] = [
            // { value: ReportTypeEnum.SALES_BY_USER, title: "Inventory Report" },
        ];

        switch (userModel.supplierUserToken?.User_Type?.id) {
            case UserTypesEnum.ADMIN:
            case UserTypesEnum.ORDER_MGMT:
            case UserTypesEnum.INVENTORY_MGMT:
                _reportOptions.push(
                    { value: ReportTypeEnum.ORDERS_DETAIL_REPORT, title: "orders_detail_report" },
                    { value: ReportTypeEnum.SALES_BY_USER, title: "sales_volume_by_user" },
                    { value: ReportTypeEnum.SALES_BY_USER_PRODUCT_DETAIL, title: "sales_volume_by_user_product_detail" },
                    { value: ReportTypeEnum.SALES_BY_PRODUCT_BY_CUSTOMER, title: "customer_product_sales" },
                    { value: ReportTypeEnum.TOTAL_LOGINS_REPORT, title: "total_logins_report" },
                    { value: ReportTypeEnum.ORDERS_CREATED, title: "orders_created_report" },
                    { value: ReportTypeEnum.ORDERS_SUBMITTED, title: "orders_submitted_report" },
                    { value: ReportTypeEnum.ITEM_IN_ORDERS_REPORT, title: "item_in_orders_report" },
                    { value: ReportTypeEnum.OPEN_ORDER_REPORT, title: "open_orders_report" }
                );
                break;
        }

        if (supplierModel) {
            if (supplierModel.has_analytics) {
                switch (userModel.supplierUserToken?.User_Type?.id) {
                    case UserTypesEnum.ADMIN:
                    case UserTypesEnum.ORDER_MGMT:
                    case UserTypesEnum.INVENTORY_MGMT:
                        _reportOptions.push({ value: ReportTypeEnum.PRODUCT_SEARCHES_BY_CUSTOMER, title: "product_searches_by_customer" });
                        break;
                }
            }

            if (supplierModel.has_serials) {
                switch (userModel.supplierUserToken?.User_Type?.id) {
                    case UserTypesEnum.ADMIN:
                    case UserTypesEnum.ORDER_MGMT:
                    case UserTypesEnum.INVENTORY_MGMT:
                        _reportOptions.push({ value: ReportTypeEnum.SERIALS_IN_ORDERS_REPORT, title: "serials_in_orders_report" });
                        break;
                }
            }

            if (supplierModel.has_commission_tiers) {
                _reportOptions.push({ value: ReportTypeEnum.COMMISSION_TIER_REPORT, title: "commission_tier_report" });
            }

            if (supplierModel.has_commissions) {
                _reportOptions.push({ value: ReportTypeEnum.COMMISSION_REPORT, title: "commission_report" });
            }

            if (supplierModel.has_rebates) {
                _reportOptions.push({ value: ReportTypeEnum.VOLUME_REBATE_REPORT, title: "volume_rebate_report" });
            }

            if (supplierModel.has_coop_advertisement) {
                _reportOptions.push({ value: ReportTypeEnum.COOP_REPORT, title: "coop_advertisement_report" });
            }

            if (supplierModel.has_spiff) {
                _reportOptions.push({ value: ReportTypeEnum.SPIFF_REPORT, title: "spiff_report" });
            }

            if (supplierModel.has_inventory) {
                _reportOptions.push({ value: ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT, title: "inventory_stock_levels_report" });
                _reportOptions.push({
                    value: ReportTypeEnum.INVENTORY_STOCK_HISTORY_TRANSACTION_REPORT,
                    title: "inventory_stock_history_transaction_report",
                });
            }

            if (props.customReports.length) {
                for (const customReport of props.customReports) {
                    _reportOptions.push({ value: ReportTypeEnum.CUSTOM, title: customReport.name, guid: customReport.guid });
                }
            }
        }

        // Admin only reports.
        switch (userModel.supplierUserToken?.User_Type?.id) {
            case UserTypesEnum.ADMIN:
                _reportOptions.push({ value: ReportTypeEnum.PRICE_GAP_REPORT, title: "price_gap_report" });
                break;
        }

        _reportOptions.push({ value: ReportTypeEnum.ACTIVITY_REPORT, title: "activity_report" });
        _reportOptions.push({ value: ReportTypeEnum.INACTIVE_ACCOUNT_REPORT, title: "inactive_account_report" });
        _reportOptions.push({ value: ReportTypeEnum.ACCOUNTS_BY_DATE_REPORT, title: "accounts_by_date_report" });

        return _reportOptions.sort((a, b) => (a.title > b.title ? 1 : -1));
    };

    const reportOptions = getReportOptions();

    const setStartDate = (moment: Moment | null) => {
        setCreatedAtStart(moment);
    };

    const setEndDate = (moment: Moment | null) => {
        setCreatedAtEnd(moment);
    };

    return (
        <div className={"ReportsReadyFilter row"}>
            <div className="row mb-2">
                <div className="col-12 col-md-2">
                    <OwLabel title={"title"} noBottomMargin={true} />
                    <OwFnInput name={"title"} value={title} fn={handleChange} onKeyDownFn={enterSearch} />
                </div>
                <div className="col-12 col-md-2">
                    <OwLabel title={"report_type"} noBottomMargin={true} />
                    <OwSelectOption
                        valueProp={"value"}
                        titleProp={"title"}
                        options={reportOptions}
                        name={"report_type"}
                        selectedOption={reportType}
                        selectedOptionProp={""}
                        hasSelectedOptionProp={true}
                        showBlankFirst={true}
                        translateTitle={true}
                        fn={handleChange}
                    />
                </div>
                <div className="col-12 col-md-4">
                    <OwLabel title={"created_at_between"} noBottomMargin={true} />
                    <OwDateRange
                        startDateSelected={createdAtStart?.toDate() || null}
                        endDateSelected={createdAtEnd?.toDate() || null}
                        startFn={setStartDate}
                        endFn={setEndDate}
                    />
                </div>
                <div className={"col-12 col-md-auto"}>
                    <OwLabel title={""} noBottomMargin={true} />
                    <div className="">
                        <OwButton title={"search"} fn={performSearch} />
                        <OwButton title={"reset"} fn={resetSearch} className={"btn-danger ms-2"} />
                    </div>
                </div>
            </div>
        </div>
    );
};
